import { useState, useCallback } from "react";
import { getLogger } from "@hoteltradergit/ht-telemetry-ui-lib";

/**
 * Custom hook for managing localStorage with React state
 * @param {string} key - The localStorage key
 * @param {*} initialValue - The initial value if no stored value exists
 * @returns {[value, setValue]} - Current value and setter function
 */
const useLocalStorage = (key, initialValue) => {
  // Get stored value from localStorage or use initial value
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      getLogger().error("useLocalStorage", {
        type: "Error",
        page_url: window.location.href,
        error_desc: "Error reading key: " + key,
        error_stack: error?.stack ?? null,
      });
      return initialValue;
    }
  });

  // Update localStorage whenever the state changes
  // Memoized with useCallback to prevent unnecessary re-renders
  const setValue = useCallback(
    (value) => {
      try {
        setStoredValue((currentValue) => {
          // Allow value to be a function so we have the same API as useState
          const valueToStore =
            typeof value === "function" ? value(currentValue) : value;

          // Save to localStorage
          window.localStorage.setItem(key, JSON.stringify(valueToStore));

          return valueToStore;
        });
      } catch (error) {
        getLogger().error("useLocalStorage", {
          type: "Error",
          page_url: window.location.href,
          error_desc: "Error setting key: " + key,
          error_stack: error?.stack ?? null,
        });
      }
    },
    [key]
  );

  return [storedValue, setValue];
};

export default useLocalStorage;
